import { useState, useEffect } from 'react';
import { X, Search, Star } from 'lucide-react';

const WatchlistModal = ({ isOpen, onClose, onSelectSymbol, stockDataMap, allData, buttonRef, onWatchlistChange, currentSymbol }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableStocks, setAvailableStocks] = useState([]);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    if (isOpen && buttonRef?.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
    
    if (isOpen) {
      const saved = localStorage.getItem('stockWatchlist');
      if (saved) {
        setWatchlist(JSON.parse(saved));
      }
      
      if (allData) {
        const uniqueSymbols = [...new Set(allData.map(item => item.symbol))];
        setAvailableStocks(uniqueSymbols);
      }
    }
  }, [isOpen, allData, buttonRef]);

  const toggleWatchlist = (symbol, e) => {
    e.stopPropagation();

    const saved = localStorage.getItem('stockWatchlist');
    let list = saved ? JSON.parse(saved) : [];

    if (list.includes(symbol)) {
      list = list.filter(s => s !== symbol);
    } else {
      list.push(symbol);
    }

    localStorage.setItem('stockWatchlist', JSON.stringify(list));
    setWatchlist(list);

    if (onWatchlistChange) {
      onWatchlistChange();
    }
  };

  const isInWatchlist = (symbol) => {
    return watchlist.includes(symbol);
  };

  const getStockIcon = (symbol) => {
    if (stockDataMap && stockDataMap.has(symbol)) {
      const stockData = stockDataMap.get(symbol);
      if (stockData.logo_url) {
        return (
          <img 
            src={stockData.logo_url} 
            alt={symbol}
            className="stock-logo w-10 h-10 rounded-full object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.textContent = 'img';
            }}
          />
        );
      }
    }
    return 'img';
  };

  const getStockInfo = (symbol) => {
    if (stockDataMap && stockDataMap.has(symbol)) {
      return stockDataMap.get(symbol);
    }
    return {
      description: symbol,
      exchange: 'Unknown'
    };
  };

  const userWatchlist = availableStocks.filter(symbol => isInWatchlist(symbol));
  
  const filteredWatchlist = searchQuery
    ? userWatchlist.filter(symbol => { 
        const info = getStockInfo(symbol);
        const searchLower = searchQuery.toLowerCase();
        return symbol.toLowerCase().includes(searchLower) || 
               info.description?.toLowerCase().includes(searchLower);
      })
    : userWatchlist;

  const sortedWatchlist = [...filteredWatchlist].sort((a, b) => a.localeCompare(b));

  if (!isOpen) return null;

  const renderStockItem = (symbol, showWatchlistButton = true) => {
    const info = getStockInfo(symbol);
    const inWatchlist = isInWatchlist(symbol);
    
    return (
      <div
        key={symbol}
        className="watchlist-item flex items-center gap-3 px-6 py-3 hover-bg-gray-50 border-b border-gray-100 last-border-b-0 transition-colors"
      >
        <button
          onClick={() => {
            onSelectSymbol(symbol);
            onClose();
          }}
          className="stock-item-button flex-1 flex items-center gap-3 min-w-0"
        >
          <div className="stock-icon-wrapper w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-xl flex-shrink-0">
            {getStockIcon(symbol)}
          </div>

          <div className="stock-details flex-1 text-left min-w-0">
            <div className="stock-symbol font-semibold text-gray-900 text-sm">
              {symbol}
            </div>
            <div className="stock-description text-xs text-gray-500 truncate">
              {info.description}
            </div>
          </div>

          <div className="stock-exchange-wrapper flex items-center gap-2 flex-shrink-0">
            <span className="stock-exchange text-xs font-medium text-gray-600">
              {info.exchange}
            </span>
          </div>
        </button>

        {showWatchlistButton && (
          <button
            onClick={(e) => toggleWatchlist(symbol, e)}
            className="watchlist-toggle-button p-1.5 hover-bg-gray-100 rounded-full transition-colors flex-shrink-0"
            title={inWatchlist ? "Remove from watchlist" : "Add to watchlist"}
          >
            <Star 
              className={`icon-md transition-colors ${
                inWatchlist 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : 'text-gray-300'
              }`}
            />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div 
        className="watchlist-overlay fixed inset-0"
        style={{ zIndex: 9998 }} 
        onClick={onClose}
      />
      
      <div 
        className="watchlist-modal fixed w-90-percent max-w-md"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 9999 
        }}
      >
        <div className="watchlist-content bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
          <div className="watchlist-header flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="watchlist-title text-xl font-bold text-gray-900">Watchlist</h2>
            <button
              onClick={onClose}
              className="close-button p-1 hover-bg-gray-100 rounded-full transition-colors"
            >
              <X className="icon-md text-gray-500" />
            </button>
          </div>

          <div className="watchlist-search px-6 py-3">
            <div className="search-input-wrapper relative">
              <Search className="search-icon absolute left-3 top-1/2 transform translate-y-neg-50 icon-sm text-gray-400" />
              <input
                type="text"
                placeholder="search watchlist..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus-outline-none focus-ring-2"
              />
            </div>
          </div>

          <div className="watchlist-items max-h-400px overflow-y-auto">
            {!searchQuery && currentSymbol && !isInWatchlist(currentSymbol) && (
              <div>
                {renderStockItem(currentSymbol, true)}
              </div>
            )}

            {sortedWatchlist.length > 0 && (
              <div>
                <div className="watchlist-divider border border-d8d8d8 mx-6"></div>
                <div className="watchlist-section-header px-6 py-2 bg-gray-50">
                  <p className="section-title text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    My Watchlist
                  </p>
                </div>
                {sortedWatchlist.map(symbol => renderStockItem(symbol, true))}
              </div>
            )}

            {!searchQuery && sortedWatchlist.length === 0 && (!currentSymbol || isInWatchlist(currentSymbol)) && (
              <div className="empty-state flex flex-col items-center justify-center py-12 text-gray-400">
                <Star className="empty-icon w-12 h-12 mb-3" />
                <p className="empty-title text-sm font-medium">Your watchlist is empty</p>
                <p className="empty-subtitle text-xs mt-1">Click the star to add stocks</p>
              </div>
            )}

            {searchQuery && sortedWatchlist.length === 0 && (
              <div className="no-results flex flex-col items-center justify-center py-12 text-gray-400">
                <Search className="no-results-icon w-12 h-12 mb-3" />
                <p className="no-results-text text-sm">No stocks found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WatchlistModal;